<script lang="ts">
import type { VariantProps } from 'class-variance-authority'
import type { PrimitiveProps } from 'reka-ui'
import type { HTMLAttributes } from 'vue'
import { cva } from 'class-variance-authority'

export const buttonVariants = cva(
  [
    'inline-flex justify-center items-center shrink-0 rounded-md font-semibold no-underline transition-colors',
    '[&_svg]:shrink-0 [&_svg]:pointer-events-none',
    'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
    'disabled:cursor-not-allowed aria-disabled:cursor-not-allowed',
  ].join(' '),
  {
    variants: {
      variant: {
        default:
          'bg-primary-900 text-neutral-100 hover:bg-primary-500 hover:text-neutral-100 disabled:bg-neutral-300 disabled:ring-neutral-600 disabled:text-neutral-600',
        success:
          'bg-accent-green text-primal-inverted hover:bg-green-800 disabled:bg-neutral-300 disabled:ring-neutral-600 disabled:text-neutral-600',
        outline:
          'ring-1 ring-inset ring-neutral-600 text-primary-900 hover:bg-neutral-600/30 hover:ring-neutral-900 disabled:bg-neutral-300 disabled:ring-0 disabled:text-neutral-600',
        destructive:
          'ring-1 ring-error text-error hover:bg-error-subtle/30 hover:ring-error disabled:bg-error-subtle/30 disabled:ring-error disabled:text-error',
        ghost:
          'bg-transparent text-primary-900 hover:bg-neutral-600/30 hover:text-primary-900 disabled:text-neutral-600 disabled:hover:bg-transparent',
        link: 'bg-transparent text-primary-900 underline-offset-4 hover:text-primary-900 hover:underline hover:decoration-2 hover:decoration-primary-900 disabled:text-neutral-600 disabled:hover:bg-transparent',
        'outline-inverted':
          'ring-1 ring-inset ring-neutral-600 text-primal-inverted hover:bg-neutral-600/30 hover:ring-white disabled:bg-neutral-300/30 disabled:ring-0 disabled:text-neutral-600',
      },
      size: {
        xs: 'px-2 py-0 h-[24px] text-xs gap-1 [&_svg]:size-[12px]',
        sm: 'px-2 py-0.5 h-[32px] text-xs gap-1.5 [&_svg]:size-4',
        md: 'px-3 py-0 h-[40px] text-sm gap-1.5 [&_svg]:size-5',
        lg: 'px-5 py-0 h-[52px] text-sm gap-2 [&_svg]:size-5',
        xl: 'px-10 py-0 h-[60px] text-[16px] gap-2 [&_svg]:size-6',
      },
      iconOnly: {
        true: 'p-0',
      },
      block: {
        true: 'w-full',
      },
    },
    compoundVariants: [
      { iconOnly: true, block: false, size: 'xs', class: 'w-[24px]' },
      { iconOnly: true, block: false, size: 'sm', class: 'w-[32px]' },
      { iconOnly: true, block: false, size: 'md', class: 'w-[40px]' },
      { iconOnly: true, block: false, size: 'lg', class: 'w-[52px]' },
      { iconOnly: true, block: false, size: 'xl', class: 'w-[60px]' },
    ],
    defaultVariants: {
      variant: 'default',
      size: 'md',
      iconOnly: false,
      block: false,
    },
  },
)

export type ButtonVariants = VariantProps<typeof buttonVariants>

export interface ButtonProps extends PrimitiveProps {
  variant?: ButtonVariants['variant']
  size?: ButtonVariants['size']
  iconOnly?: boolean
  block?: boolean
  loading?: boolean
  disabled?: boolean
  type?: 'button' | 'submit' | 'reset'
  class?: HTMLAttributes['class']
}
</script>

<script setup lang="ts">
import { Primitive } from 'reka-ui'
import { computed } from 'vue'
import { cn } from '#ui-kit/lib/utils'
import { Spinner } from '#ui-kit/ui/spinner'

const props = withDefaults(defineProps<ButtonProps>(), {
  as: 'button',
  variant: 'default',
  size: 'md',
  type: 'button',
})

const isDisabled = computed(() => props.disabled || props.loading)

const resolvedType = computed(() => {
  if (props.asChild || props.as !== 'button') {
    return undefined
  }

  return props.type
})

// Native `disabled` only works on <button>. When `asChild` renders an <a>, the
// browser still fires clicks and navigates, so guard activation explicitly.
function handleClick(event: MouseEvent) {
  if (isDisabled.value) {
    event.preventDefault()
    event.stopPropagation()
  }
}
</script>

<template>
  <Primitive
    data-slot="button"
    :as="as"
    :as-child="asChild"
    :type="resolvedType"
    :disabled="isDisabled || undefined"
    :aria-disabled="isDisabled || undefined"
    :tabindex="isDisabled ? -1 : undefined"
    :class="cn(buttonVariants({ variant, size, iconOnly, block }), props.class)"
    @click="handleClick"
  >
    <Spinner v-if="loading" />
    <!-- don't render content for iconOnly + loading buttons -->
    <slot v-if="!(loading && iconOnly)" />
  </Primitive>
</template>
